/**
 * useBlockers Hook
 * AIDLC Administrative Backend
 *
 * Provides blocker data for a project with loading/error states.
 * Supports polling interval for background refresh.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchBlockers } from '../services/blockerService';
import type { Blocker } from '../types/projects';

const DEFAULT_POLL_INTERVAL = 30000;

interface UseBlockersResult {
  blockers: Blocker[];
  openBlockers: Blocker[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useBlockers(projectId: string | null, pollInterval = DEFAULT_POLL_INTERVAL): UseBlockersResult {
  const [blockers, setBlockers] = useState<Blocker[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadBlockers = useCallback(async () => {
    if (!projectId) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchBlockers(projectId);
      setBlockers(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch blockers';
      setError(message);
      console.error('[useBlockers] Fetch failed:', err);
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (!projectId) {
      setBlockers([]);
      return;
    }

    loadBlockers();

    if (pollInterval > 0) {
      intervalRef.current = setInterval(loadBlockers, pollInterval);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [loadBlockers, pollInterval, projectId]);

  const openBlockers = blockers.filter((b) => b.status !== 'resolved');

  return { blockers, openBlockers, isLoading, error, refresh: loadBlockers };
}
